/* eslint-disable @typescript-eslint/no-explicit-any */
import slugify from "slugify";

import { getDisplayPriceWithoutCurrency } from "~/helpers/get-display-price";
import { Logger } from "~/utils/logger/browser";

import { EvergageActions, EvergageItemActions } from "./actions";
import { EvergageContentZones, EvergagePage, Tags } from "./types";

const pageSelector = (page: string) => `[data-evergage-page="${page}"]`;
const zoneSelector = (zone: string) => `[data-evergage-zone="${zone}"]`;
const categoriesSelector = '[data-evergage="breadcrumbs"] a';
const categoryLinkAttribute = "data-evergage-breadcrumbs-link";
const categoryExclusionList = [
  { keywords: "christmas", tag: Tags.christmas },
  { keywords: "halloween", tag: Tags.halloween },
  { keywords: "sale", tag: Tags.sale },
  { keywords: "click collect", tag: Tags.clickAndCollectTag },
];

const getCategories = (evergage: any, startFrom: number | null = null) =>
  evergage.resolvers.buildCategoryIdAttribute(
    categoriesSelector,
    categoryLinkAttribute,
    startFrom,
    null,
    (categoryId: string) => [
      categoryId
        ?.split("|")
        ?.map((category: string) =>
          slugify(category ?? "", { strict: true })?.toUpperCase(),
        )
        ?.join("|"),
    ],
  );

const getCategoriesFromJsonLd = (
  evergage: any,
): {
  name: string;
  categoryId: string;
}[] => {
  if (
    !evergage
      .cashDom("[data-evergage-breadcrumbs]")
      .attr("data-evergage-breadcrumbs")
  ) {
    return [];
  }
  let categories: { name: string; categoryId: string }[] = [];
  try {
    categories =
      evergage.resolvers?.fromSelectorAttribute(
        "[data-evergage-breadcrumbs]",
        "data-evergage-breadcrumbs",
        (data: any) => {
          const parsedData = JSON.parse(data);
          return parsedData
            ?.sort(
              (
                { position: pos1 }: { position: any },
                { position: pos2 }: { position: any },
              ) => pos1 - pos2,
            )
            ?.map(({ title, categoryId }: { title: any; categoryId: any }) => ({
              name: slugify(title ?? "", { strict: true }).toUpperCase(),
              categoryId,
            }));
        },
      )() || [];
  } catch (error) {
    Logger.getLogger().error("Categories not found in Breadcrumbs", error);
  }

  if (categories) {
    return categories;
  }

  Logger.getLogger().error("Categories not found in Breadcrumbs");
  return [];
};

const containsExcludedCategory = (categories: string[]): string | undefined => {
  for (const category of categories) {
    const normalizedCategory = category.toLowerCase();

    for (const exclusion of categoryExclusionList) {
      const exclusionWords = exclusion.keywords.toLowerCase().split(" ");

      if (exclusionWords.every((word) => normalizedCategory.includes(word))) {
        return exclusion.tag;
      }
    }
  }
  return undefined;
};

const getConditionalCatalogName = (evergage: any, field?: string) => {
  if (!evergage) {
    return field || "Category";
  }

  const isException = containsExcludedCategory(
    getCategoriesFromJsonLd(evergage).map((cat) => cat.name ?? ""),
  );

  return isException ? "BRAND" : field || "Category";
};

const separateNamesByCategoryId = (data: { name: string; categoryId: string }[]) => {
  const regular: string[] = [];
  const dynamic: string[] = [];

  data.forEach((item) => {
    if (typeof item.categoryId === "string") {
      regular.push(item.name);
    } else {
      dynamic.push(item.name);
    }
  });

  return { regular, dynamic };
};

const getDynamicItemAction = (evergage: any) => {
  const { regular } = separateNamesByCategoryId(getCategoriesFromJsonLd(evergage));

  if (!regular.length) {
    return EvergageItemActions.VIEW_ITEM;
  }

  return getConditionalCatalogName(evergage) === "Category"
    ? EvergageItemActions.VIEW_CATEGORY
    : EvergageItemActions.VIEW_ITEM;
};

const getDynamicCatalog = (evergage: any) => {
  const isException = containsExcludedCategory(
    getCategoriesFromJsonLd(evergage).map(
      (cat: { categoryId: string; name: string }) => cat.name,
    ),
  );
  const { regular, dynamic } = separateNamesByCategoryId(
    getCategoriesFromJsonLd(evergage),
  );

  if (isException) {
    return {
      BRAND: {
        _id: isException.toUpperCase(),
        name: isException.toUpperCase(),
      },
    };
  }

  if (regular.length) {
    return {
      Category: {
        _id: () =>
          regular.reduce(
            (acc, category) => `${acc ? `${acc}|` : ""}${category}`,
            "",
          ),
        ...(dynamic.length
          ? {
              relatedCatalogObjects: {
                BRAND: () => [
                  dynamic.reduce((acc, category) => {
                    return `${acc ? `${acc}|` : ""}${category}`;
                  }, ""),
                ],
              },
            }
          : {}),
      },
    };
  } else {
    return {
      BRAND: {
        _id: () =>
          dynamic.reduce(
            (acc, category) => `${acc ? `${acc}|` : ""}${category}`,
            "",
          ),
      },
    };
  }
};

export const evergageSitemapConfig = (evergage: any) => ({
  global: {
    onActionEvent: (actionEvent: any) => {
      const email = evergage
        .cashDom("[data-evergage-email]")
        .attr("data-evergage-email");
      const userId = evergage
        .cashDom("[data-evergage-user-id]")
        .attr("data-evergage-user-id");
      if (email && userId) {
        actionEvent.user = actionEvent.user || {};
        actionEvent.user.attributes = actionEvent.user.attributes || {};
        actionEvent.user.attributes.emailAddress = email;
        actionEvent.user.attributes.sfmcContactKey = userId;
      }
      return actionEvent;
    },
    contentZones: [{ name: "global_popup" }],
  },
  pageTypeDefault: {
    name: "default",
  },
  pageTypes: [
    {
      name: "home_page",
      action: EvergageActions.HOME_PAGE_VIEW,
      isMatch: () => location.pathname.split("/").filter((val) => val).length <= 1,
      contentZones: [
        {
          name: "home_hero_banner",
          selector: zoneSelector(EvergageContentZones.HOME_HERO_BANNER),
        },
      ],
    },
    {
      name: "product_detail",
      action: EvergageActions.PDP_VIEW,
      itemAction: EvergageItemActions.VIEW_ITEM,
      isMatch: () => location.pathname.split("/").includes("p"),
      WebConsent: true,
      contentZones: [
        {
          name: "similar_items",
          selector: zoneSelector(EvergageContentZones.CROSS_SELL),
        },
        {
          name: "footer_banner",
          selector: zoneSelector(EvergageContentZones.GENERIC_BANNER),
        },
      ],
      catalog: {
        [getConditionalCatalogName(evergage, "Product")]: {
          _id: () => {
            return evergage.util.resolveWhenTrue.bind(() => {
              const productId = evergage
                .cashDom("[data-evergage-product-id]")
                .attr("data-evergage-product-id");
              if (productId) {
                return productId;
              } else {
                return false;
              }
            });
          },
          name: evergage
            .cashDom("[data-evergage-product-name]")
            .attr("data-evergage-product-name"),
          description: evergage
            .cashDom("[data-evergage-product-description]")
            .attr("data-evergage-product-description"),
          primaryCategory: evergage
            .cashDom('[data-testautomation-id="breadcrumbs-pannel"] a')
            .eq(0)
            .attr("data-evergage-breadcrumbs-link")
            ?.toUpperCase(),
          subCategory: getCategories(evergage, 1),
          categories: getCategories(evergage),
          url: evergage.resolvers.fromHref(),
          imageUrl: evergage
            .cashDom("[data-evergage-image-url]")
            .first()
            .attr("data-evergage-image-url"),
          Stock: () => {
            const value = evergage
              .cashDom("[data-evergage-product-stock]")
              .first()
              .attr("data-evergage-product-stock");
            const numberValue = Number(value);
            if (Number.isNaN(numberValue)) {
              return undefined;
            }

            return numberValue;
          },
        },
      },
      listeners: [
        evergage.listener("click", "#share-button", () => {
          evergage.sendEvent({
            name: EvergageActions.SHARED_CATALOG_OBJECT,
            itemAction: evergage.ItemAction.Share,
            catalog: {
              Product: {
                _id: evergage
                  .cashDom("[data-evergage-product-id]")
                  .attr("data-evergage-product-id"),
                name: evergage
                  .cashDom("[data-evergage-product-name]")
                  .attr("data-evergage-product-name"),
              },
            },
          });
        }),
        evergage.listener("click", '[data-evergage="search-location"]', () => {
          evergage.sendEvent({
            action: EvergageActions.SEARCH_LOCATION,
          });
        }),
        evergage.listener("click", '[data-evergage="add-to-bag"]', () => {
          const cartData = evergage.util.buildLineItemFromPageState();
          const price = evergage.util.getFloatValue(
            evergage
              .cashDom("[data-evergage-product-price]")
              .attr("data-evergage-product-price"),
          );
          cartData.price = getDisplayPriceWithoutCurrency(String(price));
          cartData.quantity = 1;

          evergage.sendEvent({
            itemAction: evergage.ItemAction.AddToCart,
            cart: {
              singleLine: {
                Product: cartData,
              },
            },
          });
        }),
      ],
    },
    {
      name: "CLP",
      action: EvergageActions.CLP_VIEW,
      itemAction: EvergageItemActions.VIEW_CATEGORY,
      isMatch: () => {
        const arr = location.pathname.split("/");
        return (
          arr.includes("c") &&
          arr.length <= 4 &&
          evergage.cashDom(pageSelector(EvergagePage.CLP)).length > 0
        );
      },
      WebConsent: true,
      catalog: {
        [getConditionalCatalogName(evergage)]: {
          _id: () =>
            evergage.resolvers.fromHref((url: any) =>
              url.split("?")[0].split("/").pop().toUpperCase(),
            ),
          categoryName: () =>
            evergage.resolvers.fromHref((url: any) =>
              url.split("?")[0].split("/").pop().toUpperCase(),
            )(),
        },
      },
      contentZones: [
        {
          name: "Category_Hero_1",
          selector: zoneSelector(EvergageContentZones.HERO_BANNER),
        },
        {
          name: "Category_Hero_2",
          selector: zoneSelector(EvergageContentZones.COLLECTION_BANNER),
        },
        {
          name: "Category_Hero_3",
          selector: zoneSelector(EvergageContentZones.HERO_PRODUCT_CAROUSEL),
        },
        {
          name: "Category_Footer",
          selector: zoneSelector(EvergageContentZones.GENERIC_BANNER),
        },
        {
          name: "Category_Editorial_Inspirations",
          selector: zoneSelector(EvergageContentZones.EDITORIAL),
        },
      ],
    },
    {
      name: "CLP",
      action: EvergageActions.CLP_VIEW,
      itemAction: getDynamicItemAction(evergage),
      isMatch: () => {
        const arr = location.pathname.split("/");
        return (
          arr.length <= 3 &&
          evergage.cashDom(pageSelector(EvergagePage.CLP_GCP)).length > 0
        );
      },
      WebConsent: true,
      catalog: getDynamicCatalog(evergage),
      contentZones: [
        {
          name: "Category_Hero_1",
          selector: zoneSelector(EvergageContentZones.HERO_BANNER),
        },
        {
          name: "Category_Hero_2",
          selector: zoneSelector(EvergageContentZones.COLLECTION_BANNER),
        },
        {
          name: "Category_Hero_3",
          selector: zoneSelector(EvergageContentZones.HERO_PRODUCT_CAROUSEL),
        },
        {
          name: "Category_Footer",
          selector: zoneSelector(EvergageContentZones.GENERIC_BANNER),
        },
        {
          name: "Category_Editorial_Inspirations",
          selector: zoneSelector(EvergageContentZones.EDITORIAL),
        },
      ],
    },
    {
      name: "PLP",
      action: EvergageActions.PLP_VIEW,
      itemAction: getDynamicItemAction(evergage),
      isMatch: () => {
        const arr = location.pathname.split("/");
        const isPLP =
          arr.includes("c") &&
          arr.length >= 4 &&
          evergage.cashDom(pageSelector(EvergagePage.PLP)).length > 0;

        return isPLP;
      },
      WebConsent: true,
      catalog: getDynamicCatalog(evergage),
    },
    {
      name: "RBP",
      action: EvergageActions.RBP_VIEW,
      itemAction: getDynamicItemAction(evergage),
      isMatch: () => {
        const arr = location.pathname.split("/");
        const isRBP =
          arr.includes("r") &&
          arr.length >= 4 &&
          evergage.cashDom(pageSelector(EvergagePage.RBP)).length > 0;

        return isRBP;
      },
      WebConsent: true,
      catalog: getDynamicCatalog(evergage),
    },
    {
      name: "Search",
      action: () => {
        const params = new URLSearchParams(location.search);
        const value = params.get("q");
        return `Searched for ${value}`;
      },
      isMatch: () => {
        const arr = location.pathname.split("/");
        return (
          arr.includes("search") &&
          arr.length >= 3 &&
          evergage.cashDom(pageSelector(EvergagePage.SRP)).length > 0
        );
      },
      WebConsent: true,
    },
    {
      name: "order_confirmation",
      isMatch: () => location.pathname.split("/").includes("order-confirmation"),
    },
    {
      name: "article",
      action: EvergageActions.VIEW_ARTICLE,
      isMatch: () => {
        const arr = location.pathname.split("/");
        return (
          arr.includes("a") &&
          evergage.cashDom(pageSelector(EvergagePage.ARTICLE)).length > 0
        );
      },
      WebConsent: true,
      catalog: {
        Article: {
          _id: () =>
            evergage.resolvers.fromCanonical((url: any) =>
              url.split("/").pop().toUpperCase(),
            ),
          name: () =>
            evergage
              .cashDom("[data-evergage-article-name]")
              .attr("data-evergage-article-name"),
          url: evergage.resolvers.fromCanonical(),
          imageUrl: evergage
            .cashDom(`${zoneSelector(EvergageContentZones.HERO_BLOCK)} img`)
            .attr("src"),
          relatedCatalogObjects: {
            Category: [
              evergage
                .cashDom(`${zoneSelector(EvergageContentZones.HERO_BLOCK)}`)
                .attr("data-evergage-theme")
                ?.toUpperCase(),
            ],
          },
        },
      },
      contentZones: [
        {
          name: "Related_Articles",
          selector: zoneSelector(EvergageContentZones.ARTICLES_CAROUSEL),
        },
      ],
    },
    {
      name: "shopping_list",
      action: EvergageActions.VIEW_SHOPPING_LIST,
      isMatch: () =>
        evergage.cashDom(pageSelector(EvergagePage.SHOPPING_LIST)).length > 0,
    },
    {
      name: "cart",
      action: EvergageActions.VIEW_CART,
      itemAction: EvergageItemActions.VIEW_CART,
      isMatch: () =>
        evergage.cashDom(pageSelector(EvergagePage.SHOPPING_BAG)).length > 0,
    },
    {
      name: "account",
      action: EvergageActions.ACCOUNT_VIEW,
      isMatch: () => location.pathname.split("/").includes("profile"),
    },
    {
      name: "newsletter",
      action: EvergageActions.NEWSLETTER_VIEW,
      isMatch: () => {
        const values = location.pathname.split("/");

        return (
          values.includes("newsletter") || values.includes("sign-up-and-subscribe")
        );
      },
    },
    {
      name: "login",
      action: EvergageActions.LOGIN_VIEW,
      isMatch: () => location.pathname.split("/").includes("login"),
    },
  ],
});
